import { de as vuetify } from 'vuetify/locale';

/**
 * German, the dashboard's first language.
 *
 * First because the people who use it are the control room of a German
 * municipal utility, and niotix ships German and English (FIDELITY.md M16).
 * `en.ts` is typed against this object, so a key added here and forgotten there
 * is a compile error, and so is a key that exists only in English.
 *
 * Domain words stay the domain's words. "Digital Twin" and "Virtual Device" are
 * niotix product terms and appear untranslated in its German documentation;
 * translating them would make the dashboard disagree with the manual the
 * operator has open next to it.
 */
const de = {
  app: {
    name: 'StadtPuls',
    tagline: 'Betriebsübersicht',
    skipToContent: 'Zum Inhalt springen',
  },
  nav: {
    menu: 'Menü',
    overview: 'Übersicht',
    devices: 'Geräte',
    sites: 'Standorte',
  },
  settings: {
    language: 'Sprache',
    theme: 'Darstellung',
    themes: { light: 'Hell', dark: 'Dunkel', system: 'Wie System' },
  },
  pages: {
    overview: 'Übersicht',
    devices: 'Geräte',
    device: 'Gerät',
    twin: 'Standort',
    notFound: 'Seite nicht gefunden',
  },
  notFound: {
    text: 'Diese Adresse gibt es nicht, oder sie zeigt auf etwas, das Sie nicht sehen dürfen.',
    back: 'Zur Übersicht',
  },
  auth: {
    account: 'Konto',
    logout: 'Abmelden',
    unreachableTitle: 'Anmeldung nicht erreichbar',
    unreachableText:
      'Der Anmeldedienst antwortet nicht. Bitte versuchen Sie es in einem Moment erneut.',
    retry: 'Erneut versuchen',
  },
  errors: {
    network: 'Keine Verbindung zum Server.',
    unauthenticated: 'Ihre Anmeldung ist abgelaufen.',
    forbidden: 'Dafür fehlt Ihnen die Berechtigung.',
    'not-found': 'Nicht gefunden, oder nicht in Ihrem Bereich.',
    'invalid-request': 'Die Anfrage war ungültig.',
    internal: 'Der Server hat einen Fehler gemeldet.',
    retry: 'Erneut laden',
  },
  overview: {
    subtitle: 'Alle Standorte und Geräte in Ihrem Bereich',
    devices: 'Geräte',
    devicesHint: 'an einem Standort | an {n} Standorten',
    reporting: 'Melden sich',
    reportingHint: 'in den letzten {hours} Stunden',
    silent: 'Still',
    silentHint: 'länger als {hours} Stunden ohne Meldung',
    never: 'Nie gemeldet',
    neverHint: 'seit der Einrichtung',
    asOf: 'Stand {time}',
  },
  tree: {
    empty: 'In Ihrem Bereich gibt es keine Standorte.',
  },
  status: {
    reporting: 'Meldet sich',
    silent: 'Still',
    never: 'Nie gemeldet',
  },
  devices: {
    search: 'Name oder Kennung suchen',
    status: 'Status',
    name: 'Name',
    twin: 'Standort',
    externalId: 'Kennung',
    lastSeen: 'Letzte Meldung',
    tags: 'Tags',
    never: 'nie',
    unplaced: 'keinem Standort zugeordnet',
    next: 'Nächste Seite',
    first: 'Erste Seite',
    empty: 'Keine Geräte gefunden.',
    filteredByTwin: 'Nur {title} und darunter',
    clearFilter: 'Filter entfernen',
    pageNote: 'Seitenweise nach {column} sortiert, ohne Gesamtzahl.',
    sortAsc: 'aufsteigend sortiert',
    sortDesc: 'absteigend sortiert',
  },
  twin: {
    subtitle: 'Standort ohne Geräte | Standort mit einem Gerät | Standort mit {n} Geräten',
    children: 'Unterstandorte',
    devices: 'Geräte an diesem Standort und darunter',
    allDevices: 'Alle in der Geräteliste',
    masterData: 'Stammdaten',
    noMasterData: 'Keine Stammdaten hinterlegt.',
    coordinates: 'Koordinaten',
    noCoordinates: 'Keine Koordinaten, deshalb nicht auf der Karte.',
    devicesShort: 'keine Geräte | ein Gerät | {n} Geräte',
  },
  device: {
    identity: 'Gerät',
    externalId: 'Kennung',
    devEui: 'DevEUI',
    driver: 'Treiber',
    noDriver: 'kein Treiber',
    twin: 'Standort',
    tags: 'Tags',
    lastSeen: 'Letzte Meldung',
    latest: 'Aktuelle Werte',
    noValue: 'noch kein Wert',
    noVariables: 'Der Treiber dieses Geräts meldet keine Variablen.',
    yes: 'ja',
    no: 'nein',
  },
  variables: {
    temperature: 'Temperatur',
    humidity: 'Luftfeuchte',
    light: 'Helligkeit',
    motion: 'Bewegungen',
    co2: 'CO₂',
    vdd: 'Versorgungsspannung',
    volume: 'Zählerstand',
    leak: 'Leckage',
    battery_low: 'Batterie schwach',
    backflow: 'Rückfluss',
    leak_count: 'Leckagen gesamt',
    battery_v: 'Batteriespannung',
  },
  chart: {
    title: 'Verlauf',
    range: 'Zeitraum',
    variable: 'Messgröße',
    ranges: { '24h': '24 Std.', '7d': '7 Tage', '30d': '30 Tage' },
    level: 'Stand',
    consumption: 'Verbrauch je Intervall',
    empty: 'Keine Messwerte in diesem Zeitraum.',
    min: 'Min.',
    max: 'Max.',
    mean: 'Mittel',
    at: 'am {time}',
    windowMean: 'Mittelwerte je {every}',
    windowConsumption: 'Verbrauch je {every}',
    windowFlag: 'Je {every}: ob ausgelöst',
    zone: 'Zeiten in {zone}',
    device: 'Gerät',
    describe: '{what}, {span}: Minimum {min}, Maximum {max} am {at}, Mittel {mean}.',
  },
  map: {
    title: 'Karte',
    label: 'Karte der Standorte mit Geräten',
    none: 'Kein Standort mit Geräten hat Koordinaten.',
    unplaced:
      'Ein Standort mit Geräten hat keine Koordinaten und fehlt deshalb auf der Karte: | {n} Standorte mit Geräten haben keine Koordinaten und fehlen deshalb auf der Karte:',
  },
  $vuetify: vuetify,
};

export type Messages = typeof de;
export default de;
